import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertCircle,
  RefreshCw,
  Navigation,
  CheckCircle,
  MapPin,
  Play,
} from 'lucide-react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
        // Route-specific error variants for geometry issues
        'geometry-missing':
          'bg-red-50 border-red-200 text-red-800 [&>svg]:text-red-600 *:data-[slot=alert-description]:text-red-700',
        'geometry-invalid':
          'bg-orange-50 border-orange-200 text-orange-800 [&>svg]:text-orange-600 *:data-[slot=alert-description]:text-orange-700',
        'optimization-required':
          'bg-blue-50 border-blue-200 text-blue-800 [&>svg]:text-blue-600 *:data-[slot=alert-description]:text-blue-700',
        'geometry-approximate':
          'bg-yellow-50 border-yellow-200 text-yellow-800 [&>svg]:text-yellow-600 *:data-[slot=alert-description]:text-yellow-700',
        'geometry-success':
          'bg-green-50 border-green-200 text-green-800 [&>svg]:text-green-600 *:data-[slot=alert-description]:text-green-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  )
}

// Route Geometry Error Types
export type RouteGeometryError =
  | 'optimization-required'
  | 'geometry-missing'
  | 'geometry-invalid'
  | 'decoding-failed'
  | 'network-error'
  | 'geometry-approximate'
  | 'geometry-success'

// Route Geometry Alert Component
interface RouteGeometryAlertProps {
  error: RouteGeometryError
  routeName?: string
  technicalDetails?: string
  onRetry?: () => void
  onOptimize?: () => void
  className?: string
  compact?: boolean
}

function RouteGeometryAlert({
  error,
  routeName,
  technicalDetails,
  onRetry,
  onOptimize,
  className,
  compact = false,
}: RouteGeometryAlertProps) {
  const errorConfig = {
    'optimization-required': {
      variant: 'optimization-required' as const,
      icon: <Play className="w-4 h-4" />,
      title: 'Route Not Yet Optimized',
      message:
        'This route needs to be optimized to generate accurate path geometry.',
      action: onOptimize ? 'Run Optimization' : undefined,
      actionHandler: onOptimize,
    },
    'geometry-missing': {
      variant: 'geometry-missing' as const,
      icon: <AlertCircle className="w-4 h-4" />,
      title: 'Route Path Missing',
      message:
        'The optimization engine did not provide route path geometry. Route steps are displayed as individual points.',
      action: onRetry ? 'Retry' : undefined,
      actionHandler: onRetry,
    },
    'geometry-invalid': {
      variant: 'geometry-invalid' as const,
      icon: <AlertCircle className="w-4 h-4" />,
      title: 'Invalid Route Data',
      message:
        'The route geometry data is corrupted or malformed and cannot be displayed properly.',
      action: onRetry ? 'Retry' : undefined,
      actionHandler: onRetry,
    },
    'decoding-failed': {
      variant: 'geometry-invalid' as const,
      icon: <AlertCircle className="w-4 h-4" />,
      title: 'Route Decoding Failed',
      message:
        'Failed to decode the route geometry data. The route path cannot be displayed.',
      action: onRetry ? 'Retry' : undefined,
      actionHandler: onRetry,
    },
    'network-error': {
      variant: 'geometry-invalid' as const,
      icon: <RefreshCw className="w-4 h-4" />,
      title: 'Network Error',
      message: 'Failed to load route geometry due to network issues.',
      action: onRetry ? 'Retry' : undefined,
      actionHandler: onRetry,
    },
    'geometry-approximate': {
      variant: 'geometry-approximate' as const,
      icon: <Navigation className="w-4 h-4" />,
      title: 'Approximate Route Path',
      message:
        'Displaying straight-line approximation between route stops. Run optimization for accurate road-based paths.',
      action: onOptimize ? 'Optimize for Accuracy' : undefined,
      actionHandler: onOptimize,
    },
    'geometry-success': {
      variant: 'geometry-success' as const,
      icon: <CheckCircle className="w-4 h-4" />,
      title: 'Accurate Route Path',
      message:
        'Displaying road-accurate route geometry from optimization engine.',
      action: undefined,
      actionHandler: undefined,
    },
  }

  const config = errorConfig[error]
  const [showTechnical, setShowTechnical] = React.useState(false)

  return (
    <Alert variant={config.variant} className={className}>
      {config.icon}
      <AlertTitle className={compact ? 'text-sm' : undefined}>
        {routeName ? `${routeName}: ${config.title}` : config.title}
      </AlertTitle>
      <AlertDescription className={compact ? 'text-sm' : undefined}>
        {!compact && <p>{config.message}</p>}

        {config.action && config.actionHandler && (
          <button
            onClick={config.actionHandler}
            className="mt-2 px-4 py-2 text-sm font-semibold bg-white/50 hover:bg-white/75 rounded border transition-colors"
          >
            {config.action}
          </button>
        )}

        {technicalDetails && (
          <div className="mt-2">
            <button
              onClick={() => setShowTechnical(!showTechnical)}
              className="text-sm underline hover:no-underline"
            >
              {showTechnical ? 'Hide' : 'Show'} Technical Details
            </button>
            {showTechnical && (
              <div className="mt-2 p-2 bg-black/5 rounded text-sm font-mono">
                {technicalDetails}
              </div>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}

export {
  Alert,
  AlertTitle,
  AlertDescription,
  RouteGeometryAlert,
  type RouteGeometryError,
}
